import { useCallback, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Icon } from '@forecastify/ui';
import { useT } from '../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ DROP ANYWHERE ON THE SUBJECT — AND OVER A FIELD, INTO THAT FIELD.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Asked for on 14 Sep, copied from Jira: the whole detail surface accepts a file and attaches it to
 * the ENTITY, but a file dragged over the description or the comment box lands *there* instead. One
 * gesture, and where you release it decides what it becomes.
 *
 * ⚠️⚠️ **THE TARGET IS RESOLVED FROM THE POINTER, NOT FROM A CONTEXT OF REGISTERED REGIONS.** The
 * obvious build is a provider plus `useDropRegion()` in every field, each registering and
 * unregistering on enter and leave. That design has a bug it cannot avoid: `dragleave` fires when
 * the pointer crosses onto a CHILD of the region, so a composer containing a toolbar de-registers
 * itself the moment the pointer passes over a button, and the drop silently becomes the entity's.
 * Depth counting per region fixes it and puts a counter in every field.
 *
 * Reading `document.elementFromPoint` at the moment of the drop has none of that: there is one
 * answer, it is the one under the cursor, and nesting resolves by walking up. A region opts in with
 * a single `data-drop-zone` attribute and no hook at all.
 *
 * ⚠️ **The veil follows the same lookup on `dragover`**, so what it says and what happens cannot
 * disagree — they are one function called twice.
 *
 * ⚠️ **`pointer-events-none` on the veil is load-bearing.** It appears mid-gesture under the
 * pointer; an element that took pointer events would become the answer to `elementFromPoint` and
 * every drop would resolve to the veil itself. `agent-screen.tsx` records the flicker half of the
 * same rule.
 */

/** The attribute a field marks itself with. The value is the zone's name, echoed back on drop. */
export const DROP_ZONE_ATTRIBUTE = 'data-drop-zone';

/**
 * ⚠️ **Exported so a caller writes `{...dropZone('comment')}` rather than the raw attribute.** A
 * hand-written `data-drop-zone` that is misspelled fails silently — the region simply never wins —
 * and a helper is the cheapest place to make that impossible.
 */
export function dropZone(name: string): Record<string, string> {
  return { [DROP_ZONE_ATTRIBUTE]: name };
}

/**
 * ⚠️ **The ELEMENT, not only its name — item 4, 15 Sep.** *"A nice card preview on the affected
 * area showing where the attachment will be dropped."* Naming the destination in a box in the
 * middle of the screen tells you the answer; tracing the destination shows you it, and the two
 * cost the same lookup. The rect is read at the moment of the move, so a zone that scrolled or
 * resized under the drag is still framed where it actually is.
 */
function zoneUnder(x: number, y: number): Element | null {
  const element = document.elementFromPoint(x, y);
  /* ⚠️ `closest`, so a drop on a toolbar button inside the composer still resolves to the
     composer. This is the whole reason the pointer is the source of truth. */
  return element?.closest(`[${DROP_ZONE_ATTRIBUTE}]`) ?? null;
}

function nameOf(zone: Element | null): string | null {
  return zone?.getAttribute(DROP_ZONE_ATTRIBUTE) ?? null;
}

/** Where a zone is on screen, so the veil can draw its frame there. */
interface ZoneFrame {
  readonly name: string;
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

function frameOf(zone: Element | null): ZoneFrame | null {
  const name = nameOf(zone);
  if (zone === null || name === null) return null;
  const rect = zone.getBoundingClientRect();
  return { name, top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

/**
 * ⚠️ **A drag carrying files, and not a drag of text or of one of our own rows.** Without this the
 * veil appears every time somebody reorders a board card. `file-drop.ts` makes the same check and
 * carries the longer note on why `types` rather than `items`.
 */
function carriesFiles(transfer: DataTransfer | null): boolean {
  return transfer !== null && [...transfer.types].includes('Files');
}

/** ⚠️ Value equality, so a pointer move that changes nothing does not re-render the veil. */
function sameFrame(a: ZoneFrame | null, b: ZoneFrame | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.name === b.name &&
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height
  );
}

export function ScopedDropSurface({
  /** What the entity is called in the veil — already translated. */
  entityLabel,
  /** Zone name → what the veil should say while the pointer is over it. Already translated. */
  zoneLabels,
  /**
   * `zone` is `null` for a drop that landed on the surface rather than on a field, which is the
   * entity's own folder. Every file the drop carried, unfiltered — what is acceptable is the
   * caller's question.
   */
  onFiles,
  /** ⚠️ A disabled surface still SWALLOWS the drop — see `file-drop.ts`. */
  enabled = true,
  className,
  children,
}: {
  readonly entityLabel: string;
  readonly zoneLabels: Readonly<Record<string, string>>;
  readonly onFiles: (files: readonly File[], zone: string | null) => void;
  readonly enabled?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  const t = useT();
  const [over, setOver] = useState(false);
  /* ⚠️ The whole frame, not just the name — see `frameOf`. `null` is *the entity*, which is the
     surface itself and needs no frame of its own. */
  const [zone, setZone] = useState<ZoneFrame | null>(null);
  /**
   * ⚠️ **Depth counting on the SURFACE, which is the one place it is unavoidable.** `dragenter`
   * and `dragleave` fire for every element the pointer crosses, so a plain boolean flickers off
   * the instant the drag moves between two children. One counter here replaces one per region.
   */
  const depth = useRef(0);
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE VEIL IS THE SIZE OF THIS SURFACE, NOT OF THE WINDOW — 15 Sep.    │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * It was `fixed inset-0`: dragging a file over a task open in a MODAL greyed the whole screen
   * and drew a dashed frame around the browser window, which says *drop anywhere* about a surface
   * that owns a third of it. Reported with a screenshot of exactly that.
   *
   * ⚠️ **Measured, not styled.** `absolute inset-0` on the surface would be the obvious fix and is
   * wrong twice: the surface scrolls, so an absolutely-positioned child scrolls away with the
   * content, and it would need a `relative` parent this component does not own. The rect is read
   * on `dragenter` and again on every move — the same treatment the ZONE frame gets, and for the
   * same reason.
   */
  const surface = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState<ZoneFrame | null>(null);

  /* ⚠️ Its own name, so `sameFrame` can compare it — it is never rendered. */
  const measure = useCallback(() => {
    const element = surface.current;
    if (element === null) return;
    const rect = element.getBoundingClientRect();
    const next: ZoneFrame = {
      name: '',
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };
    setFrame((current) => (sameFrame(current, next) ? current : next));
  }, []);

  const clear = useCallback(() => {
    depth.current = 0;
    setOver(false);
    setZone(null);
  }, []);

  return (
    <div
      ref={surface}
      className={className}
      onDragEnter={(event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event.dataTransfer)) return;
        event.preventDefault();
        depth.current += 1;
        setOver(true);
        measure();
      }}
      onDragOver={(event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event.dataTransfer)) return;
        /* ⚠️ Without `preventDefault` on EVERY dragover the browser refuses the drop and navigates
           the tab to the file, which takes the whole page and anything unsaved with it. */
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        /* ⚠️ Compared by VALUE, not by identity: `frameOf` builds a fresh object per move, so
           setting it unconditionally re-renders the veil on every pointer event. */
        const next = frameOf(zoneUnder(event.clientX, event.clientY));
        setZone((current) => (sameFrame(current, next) ? current : next));
        /* ⚠️ Re-read on every move, so a scroll mid-drag cannot leave the veil behind. */
        measure();
      }}
      onDragLeave={(event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event.dataTransfer)) return;
        depth.current -= 1;
        if (depth.current <= 0) clear();
      }}
      onDrop={(event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event.dataTransfer)) return;
        event.preventDefault();
        const landed = nameOf(zoneUnder(event.clientX, event.clientY));
        const files = [...event.dataTransfer.files];
        clear();
        if (!enabled || files.length === 0) return;
        onFiles(files, landed);
      }}
    >
      {children}

      {/**
       * ⚠️ **Rendered only while a drag is over, rather than always-mounted at zero opacity.** The
       * drag either is or is not over the surface; an always-present overlay is one more element
       * between the reader and the page for every second nobody is dragging anything.
       *
       * ⚠️ **It names the DESTINATION**, which is the entire point of the feature: without the
       * label the two behaviours are indistinguishable until after the file has landed somewhere.
       */}
      {!over || frame === null ? null : (
        <div
          className="pointer-events-none fixed z-50 p-4"
          /* ⚠️ This surface's own rectangle — see `surface` above. On a full page that IS the
             window; in a modal or a drawer it is the modal or the drawer. */
          style={{ top: frame.top, left: frame.left, width: frame.width, height: frame.height }}
        >
          <div className="bg-background/80 absolute inset-0" />
          {/**
           * ┌───────────────────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ THE FRAME IS THE SHAPE OF THE DESTINATION — items 4 and 15, 15 Sep.  │
           * └───────────────────────────────────────────────────────────────────────────┘
           *
           * It was a 300px card in the middle of the screen whichever destination was selected,
           * so the one thing the reader had to know — *entity, or this field?* — was carried by a
           * word inside a box that never moved. Now the box IS the answer: over nothing in
           * particular it traces the whole surface, and over a field it snaps to that field's own
           * rectangle.
           *
           * ⚠️ **`fixed` coordinates, because `getBoundingClientRect` is viewport-relative.** The
           * veil is a `fixed` layer for exactly this reason: no offset parent to subtract, and a
           * scroll mid-drag cannot leave the frame behind — the rect is re-read on every move.
           */}
          {zone === null ? (
            <div className="border-primary bg-primary/[0.06] relative flex h-full w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-10 py-8">
              <Icon name="upload" size={22} className="text-primary" />
              <p className="text-[13px] font-medium">{t('drop.toEntity', { name: entityLabel })}</p>
              <p className="text-muted-foreground text-[11.5px]">{t('drop.hint')}</p>
            </div>
          ) : (
            <div
              className="border-primary bg-primary/[0.08] fixed flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed"
              style={{
                /* ⚠️ Four pixels of bleed so the frame reads as *around* the field rather than as a
                   border somebody added to it. */
                top: zone.top - 4,
                left: zone.left - 4,
                width: zone.width + 8,
                height: zone.height + 8,
              }}
            >
              <Icon name="upload" size={18} className="text-primary" />
              {/* ⚠️ The label is hidden on a short field: two lines of text in a 32px box is a
                  smear, and the frame alone already says where the file lands. */}
              {zone.height < 56 ? null : (
                <p className="text-[12.5px] font-medium">
                  {t('drop.toZone', { name: zoneLabels[zone.name] ?? entityLabel })}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
